import { DragDropModule } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { SharedModule } from '../../../../../../../app.module.shared';
import { NccData, NccSearchFilter } from '../../../../../../../models';
import { nccSearchAllRequest, nccSearchDataReset, nccSearchRequest } from '../../../../../../../store/ncc/actions-ncc';
import { getFoundNccs } from '../../../../../../../store/ncc/selectors-ncc';
import { AppStore } from '../../../../../../../store/store';
import { CountrySelectComponent } from '../../../../../../shared/input-country/input-country.component';
import { LoadingSpinnerComponent } from '../../../../../../shared/loading-spinner/loading-spinner.component';
import { RegionSelectComponent } from '../../../../../../shared/region-select/region-select.component';

@Component({
  selector: 'app-ncc-search-dialog',
  standalone: true,
  imports: [MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSortModule,
    MatDialogModule,
    DragDropModule,
    SharedModule,
    RegionSelectComponent,
    LoadingSpinnerComponent,
    CountrySelectComponent
  ],
  templateUrl: './ncc-search-dialog.component.html'
})
export class NccSearchDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  searchForm!: FormGroup;
  readonly displayedColumns: string[] = ['name', 'region', 'actions'];
  private dataSourceSubject = new BehaviorSubject<MatTableDataSource<NccData>>(new MatTableDataSource<NccData>());
  dataSource$ = this.dataSourceSubject.asObservable();
  searchSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppStore>,
    private dialogRef: MatDialogRef<NccSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.initializeSearchForm();
    this.searchSubscription = this.store.select(getFoundNccs)
      .pipe(map(foundNccs => this.generateDataTableSource(foundNccs)))
      .subscribe(dataSource => this.dataSourceSubject.next(dataSource));
  }

  ngAfterViewInit(): void {
    this.dataSource$.subscribe(dataSource => {
      if (dataSource) {
        dataSource.paginator = this.paginator;
        dataSource.sort = this.sort;
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  onSearch() {
    const searchFilter = this.getSearchFilterFromForm();
    if (this.isEmptySearchFilter(searchFilter)) {
      this.store.dispatch(nccSearchAllRequest());
    } else {
      this.store.dispatch(nccSearchRequest({ searchFilter }));
    }
  }

  onClearFilter() {
    this.searchForm.reset();
    this.store.dispatch(nccSearchDataReset());
  }

  pickNcc(ncc: NccData) {

    this.dialogRef.close(ncc);

  }

  cancel() {
    this.dialogRef.close();
  }

  private initializeSearchForm() {
    this.searchForm = this.fb.group({
      name: [null],
      region: [null],
      nation: [null],
      evaneosCode: [null]
    });
  }

  private getSearchFilterFromForm(): NccSearchFilter {
    const { region, nation, name, evaneosCode } = this.searchForm.value;
    const filter: NccSearchFilter = {};
    if (region) {
      filter.region = region;
    }
    if (nation) {
      filter.nation = nation.code;
    }
    if (name) {
      filter.ncc = name;
    }
    if (evaneosCode) {
      filter.evaneosCode = evaneosCode;
    }

    return filter;
  }

  private generateDataTableSource(foundNccs: NccData[]): MatTableDataSource<NccData> {
    const matTableDataSource = new MatTableDataSource<NccData>(foundNccs);
    return matTableDataSource;
  }

  private isEmptySearchFilter(searchFilter: NccSearchFilter) {
    return Object.values(searchFilter).every(value => !value);
  }
}